import React from "react";
//#region types
import { PaymentEnums } from "models/enums";
import { UnhandledBankPaymentsListProps } from "models/propTypes/paymentPropTypes";
import { TranslationKeys } from "translations";
//#endregion
//#region components
import { SimpleViewPanel } from "components/common";
import IncomingInvoicePaymentList from "./incomingInvoicePaymentList";
import ReceiptList from "./receiptList";
import PayoutFilesList from "./payoutList";

//#endregion
import { LocalizationHelperInstance, PaymentHelper } from "utils/helpers";

const UnhandledBankPaymentsList: React.FC<UnhandledBankPaymentsListProps> = (props) => {
  const { bankPaymentInformation, selectedPayments, onUserProfileClick } = props;

  const onPaymentsListUpdate = (paymentType: PaymentEnums.BankPaymentType, rows: any[], payout?: any) => {
    let updatedPayments = { ...selectedPayments };
    if (
      paymentType === PaymentEnums.BankPaymentType.IncomingInvoicePaymentFile ||
      paymentType === PaymentEnums.BankPaymentType.ReceiptPayoutFile
    ) {
      let updatedPayoutItems = selectedPayments ? PaymentHelper.getPaymentsByType(paymentType, selectedPayments) : [];
      if (rows.length > 0) {
        updatedPayoutItems?.push(payout);
      } else {
        // eslint-disable-next-line eqeqeq --- disabling to avoid regression testing if changed
        updatedPayoutItems = updatedPayoutItems?.filter((i) => i.id != payout.id);
      }
      updatedPayments = PaymentHelper.updatePaymentsByType(paymentType, updatedPayments, updatedPayoutItems);
    } else {
      updatedPayments = PaymentHelper.updatePaymentsByType(paymentType, updatedPayments, rows);
    }

    props.onPaymentSelectionsChange && props.onPaymentSelectionsChange(updatedPayments);
  };

  const incomingInvoicePaymentFilesCount = bankPaymentInformation?.incomingInvoicePaymentFilesToSign?.length ?? 0;
  const receiptPayoutFilesCount = bankPaymentInformation?.receiptPayoutFilesToSign?.length ?? 0;

  return (
    <React.Fragment>
      {bankPaymentInformation?.incomingInvoiceManualPayments &&
        bankPaymentInformation?.incomingInvoiceManualPayments.length > 0 && (
          <SimpleViewPanel
            appearance="sm"
            title={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.IncomingInvoices)}
          >
            <IncomingInvoicePaymentList
              items={props.bankPaymentInformation?.incomingInvoiceManualPayments?.map(
                (i) =>
                  // eslint-disable-next-line eqeqeq --- since checked against different types
                  ({ ...i, select: selectedPayments?.incomingInvoiceManualPayments?.some((s) => s.id == i.id) } as any)
              )}
              onRowSelectionsChange={onPaymentsListUpdate.bind(
                null,
                PaymentEnums.BankPaymentType.IncomingInvoiceManualPayment
              )}
              onUserProfileClick={onUserProfileClick}
            ></IncomingInvoicePaymentList>
          </SimpleViewPanel>
        )}
      {bankPaymentInformation?.incomingInvoicePaymentFilesToSign?.map((f, index) => (
        <SimpleViewPanel
          appearance="sm"
          title={`${LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Payments.PaymentFile)} ${
            incomingInvoicePaymentFilesCount > 1 ? index + 1 : ""
          }`}
        >
          <IncomingInvoicePaymentList
            items={f.incomingInvoicePayments?.map(
              (i) =>
                ({
                  ...i,
                  select: selectedPayments?.incomingInvoicePaymentFilesToSign
                    ?.find((ip) => ip.id === f.id)
                    ?.incomingInvoicePayments?.some((s) => s.id === i.id),
                } as any)
            )}
            onRowSelectionsChange={(rows: any[]) => {
              onPaymentsListUpdate(PaymentEnums.BankPaymentType.IncomingInvoicePaymentFile, rows, f);
            }}
            disableRowSelection={true}
            onUserProfileClick={onUserProfileClick}
          ></IncomingInvoicePaymentList>
        </SimpleViewPanel>
      ))}

      {bankPaymentInformation?.receiptPayoutFilesToSign?.map((r, index) => (
        <SimpleViewPanel
          appearance="sm"
          title={`${LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Payments.ReceiptPaymentFile)} ${
            receiptPayoutFilesCount > 1 ? index + 1 : ""
          }`}
        >
          <ReceiptList
            items={r.receipts?.map(
              (receipt) =>
                ({
                  ...receipt,
                  select: selectedPayments?.receiptPayoutFilesToSign
                    ?.find((rp) => rp.id === r.id)
                    ?.receipts?.some((s) => s.receiptId === receipt.receiptId),
                } as any)
            )}
            onRowSelectionsChange={(rows: any[]) => {
              onPaymentsListUpdate(PaymentEnums.BankPaymentType.ReceiptPayoutFile, rows, r);
            }}
            disableRowSelection={true}
            payout={r}
          ></ReceiptList>
        </SimpleViewPanel>
      ))}
      {bankPaymentInformation?.salaryPayoutFilesToSign &&
        bankPaymentInformation?.salaryPayoutFilesToSign.length > 0 && (
          <SimpleViewPanel
            appearance="sm"
            title={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Payments.SalaryPayouts)}
          >
            <PayoutFilesList
              items={props.bankPaymentInformation?.salaryPayoutFilesToSign?.map(
                (s) =>
                  ({ ...s, select: selectedPayments?.salaryPayoutFilesToSign?.some((sp) => sp.id === s.id) } as any)
              )}
              onRowSelectionsChange={onPaymentsListUpdate.bind(null, PaymentEnums.BankPaymentType.SalaryPayout)}
            ></PayoutFilesList>
          </SimpleViewPanel>
        )}

      {bankPaymentInformation?.manualPayoutsToSign && bankPaymentInformation?.manualPayoutsToSign.length > 0 && (
        <SimpleViewPanel
          appearance="sm"
          title={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Payments.ManualPayouts)}
        >
          <PayoutFilesList
            items={props.bankPaymentInformation?.manualPayoutsToSign?.map(
              (m) => ({ ...m, select: selectedPayments?.manualPayoutsToSign?.some((s) => s.id === m.id) } as any)
            )}
            onRowSelectionsChange={onPaymentsListUpdate.bind(null, PaymentEnums.BankPaymentType.ManualPayout)}
          ></PayoutFilesList>
        </SimpleViewPanel>
      )}
    </React.Fragment>
  );
};

export default UnhandledBankPaymentsList;
